import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableCell,
  TableBody,
  TableRow,
  Grid,
  IconButton,
  TextField,
} from '@mui/material';
import { LightTooltip } from '../../../../../../../../../lib/designSystem/WhiteTooltip';
import { Info, KeyboardArrowDown } from '@mui/icons-material';
import ArrearPopper from '../../../../../common/parameters/components/financeHubForm/components/arrearPopper';
import {
  formatMoney,
  formatMoneyBack,
} from '../../../../../../../../../lib/functions/utils';
import SimulationTraditional from './components/SimulationTraditional';
import EnhancedTable from '../../../../../../../../../lib/designSystem/Table';
import OperationResume from '../OperationResume';

const gridStyle = {
  alignContent: 'center',
  alignItems: 'center',
  padding: '4px',
  paddingRight: 23,
};

const calculateRequestDetailsTraditional = (
  requestForCreation: any,
  expensesHasIVA: boolean,
) => {
  const formData = {
    advance: requestForCreation.advance,
    rate: requestForCreation.rate,
    operationCostComissionFixed:
      requestForCreation.operationCostComissionFixed ||
      requestForCreation.operationCostFixed ||
      0,
    operationCostComissionVariable:
      requestForCreation.operationCostComissionVariable ||
      requestForCreation.operationCostVariable ||
      0,
    expensesCost: requestForCreation.expensesCost || 0,
    arrearType: requestForCreation.arrearType || 'lineal',
    arrearTableId: requestForCreation.arrearTableId || null,
  };

  let operationAmount = 0;
  let operationAdvanced = 0;
  let operationCostRate = 0;
  const documentsWithDetails = [];
  for (const document of requestForCreation.documents) {
    const documentAmount = document.amount * (document.active ? 1 : 0);
    const { daysToCount } = document;
    const amountFinanced = Math.round(
      documentAmount * (formData.advance / 100),
    );
    const amountRetention = Math.round(
      documentAmount * (1 - formData.advance / 100),
    );
    const amountDocument = documentAmount - amountRetention;
    const costRate = daysToCount
      ? Math.round((daysToCount / 30) * (formData.rate / 100) * amountFinanced)
      : 0;
    operationAmount += documentAmount;
    operationAdvanced += amountFinanced;
    operationCostRate += costRate;
    const amountToDeposit = amountFinanced;

    documentsWithDetails.push({
      ...document,
      amountDocument,
      amountFinanced,
      amountRetention,
      amountToDeposit,
      costRate,
    });
  }
  const operationIva = Math.round(
    0.19 *
      (formData.operationCostComissionFixed +
        Math.round(formData.operationCostComissionVariable)),
  );

  const expensesIva = expensesHasIVA
    ? Math.round(0.19 * formData.expensesCost)
    : 0;
  const operationToDeposit = operationAdvanced || 0;
  const operationAccountReceivable =
    (formData.operationCostComissionFixed || 0) +
    (formData.operationCostComissionVariable || 0) +
    (formData.expensesCost || 0) +
    (operationCostRate || 0) +
    (operationIva || 0) +
    (expensesIva || 0);
  return {
    advance: formData.advance,
    rate: formData.rate,
    operationAmount,
    operationAdvanced,
    operationCostRate,
    operationToDeposit,
    operationAccountReceivable,
    operationIva,
    expensesIva,
    operationCostComissionFixed: formData.operationCostComissionFixed,
    operationCostComissionVariable: formData.operationCostComissionVariable,
    expensesCost: formData.expensesCost,
    documentsWithDetails,
    arrearType: formData.arrearType,
    arrearTableId: formData.arrearTableId,
  };
};

export const CustomTooltip = (text: Array<Array<string>>) => (
  <div style={{ padding: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 10 }}>
      <Typography variant="body1">Descripción</Typography>
    </div>
    <Table size="small">
      <TableBody>
        {text.map((row) => (
          <TableRow key={row[0]}>
            <TableCell key={row[0]}>{row[0]}</TableCell>
            <TableCell key={row[1]}>{row[1]}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const Simulator = (props: any) => {
  const {
    requestForCreation,
    setRequestForCreation,
    availableArrearTables,
    requestConfiguration,
  } = props;

  const [openArrearPopper, setOpenArrearPopper] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [operationDetailsTraditional, setOperationDetailsTraditional] =
    useState<any>({});
  const inRange = (value: number | string) => {
    return Number(value) >= 0 && Number(value) <= 100;
  };

  const handleChange = (id: string, newValue: string) => {
    const moneyIds = ['operationCostComissionFixed', 'expensesCost'];
    const value = moneyIds.includes(id)
      ? formatMoneyBack(newValue) || 0
      : newValue;
    if (isNaN(Number(value)) && id !== 'arrearType' && id !== 'arrearTableId')
      return;
    if (id === 'advance' && !inRange(value)) return;
    if (id === 'operationCostComissionVariable') {
      if (!inRange(value)) return;
      const variableAmount = requestForCreation.amount * (Number(value) / 100);
      return setRequestForCreation({
        ...requestForCreation,
        operationCostComissionVariable: variableAmount,
        operationCostComissionVariablePercentage: value,
      });
    }
    if (id === 'rate' && !inRange(value)) return;
    if (id === 'arrearRate' && !inRange(value)) return;
    return setRequestForCreation({
      ...requestForCreation,
      [id]: value,
    });
  };

  const updateSimulationValues = () => {
    const {
      advance,
      rate,
      operationAmount,
      operationAdvanced,
      operationCostRate,
      operationToDeposit,
      operationAccountReceivable,
      operationIva,
      expensesIva,
      operationCostComissionFixed,
      operationCostComissionVariable,
      expensesCost,
      documentsWithDetails,
    } = calculateRequestDetailsTraditional(
      requestForCreation,
      requestConfiguration.expensesHasIVA,
    );
    if (
      JSON.stringify(requestForCreation.documents) !==
      JSON.stringify(documentsWithDetails)
    ) {
      setRequestForCreation({
        ...requestForCreation,
        documents: documentsWithDetails,
      });
    }
    setOperationDetailsTraditional({
      operationAmount,
      operationAdvanced,
      operationCostRate,
      operationToDeposit,
      operationAccountReceivable,
      operationIva,
      expensesCost,
      expensesIva,
      operationCostComissionFixed,
      operationCostComissionVariable,
      advance,
      rate,
    });
  };

  useEffect(() => {
    updateSimulationValues();
  }, [requestForCreation]);

  return (
    <div>
      <Grid container>
        <Grid item xs={6}>
          <div
            style={{
              paddingRight: '10px',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <Typography fontWeight="bold" variant="h6">
              Resumen solicitud
            </Typography>
            <OperationResume
              stakeholderName={requestForCreation.stakeholderName}
              stakeholderIdentifier={requestForCreation.stakeholderIdentifier}
              documents={requestForCreation.documents}
              productType={requestForCreation.productType}
              requestDate={requestForCreation.requestDate.split('T')[0]}
            />
            <div style={{ padding: '20px 0px' }}>
              <Grid
                container
                style={gridStyle}
                columnSpacing={3}
                rowSpacing={1}
              >
                <Grid item xs={6}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography fontWeight={600}>Montos</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Anticipo',
                          'Porcentaje a financiar respecto al total del valor de la operación.',
                        ],
                      ])}
                    >
                      <Info
                        style={{
                          marginLeft: 6,
                          fontSize: 14,
                        }}
                      />
                    </LightTooltip>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontWeight={600}>Tasas</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Interés',
                          'Interés aplicado a la operación. Se descuenta del monto a financiar como diferencia de precio.',
                        ],
                        [
                          'Interés mora',
                          'Interés aplicado por cada día de mora en incrementos diarios.',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">Anticipo (%) </Typography>
                  <TextField
                    value={requestForCreation.advance}
                    placeholder="100"
                    type="number"
                    fullWidth
                    disabled={requestForCreation.productType === 'traditional'}
                    style={{ padding: '0px !important' }}
                    onChange={(e) => handleChange('advance', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body1">Interés (%)</Typography>
                  <TextField
                    value={requestForCreation.rate}
                    type="number"
                    fullWidth
                    placeholder="2.2"
                    onChange={(e) => handleChange('rate', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body1">Interés mora (%)</Typography>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      value={
                        requestForCreation.arrearType === 'table'
                          ? 'Tabla'
                          : requestForCreation.arrearRate
                      }
                      type={
                        requestForCreation.arrearType === 'table'
                          ? 'text'
                          : 'number'
                      }
                      fullWidth
                      disabled={requestForCreation.arrearType === 'table'}
                      placeholder="2.2"
                      onChange={(e) =>
                        handleChange('arrearRate', e.target.value)
                      }
                    />
                    {availableArrearTables.length > 0 && (
                      <>
                        <IconButton
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setOpenArrearPopper(true);
                          }}
                          // color={formErrors?.arrearTableId ? 'error' : undefined}
                        >
                          <KeyboardArrowDown />
                        </IconButton>
                        <ArrearPopper
                          openArrearPopper={openArrearPopper}
                          setOpenArrearPopper={setOpenArrearPopper}
                          anchorEl={anchorEl}
                          formData={requestForCreation}
                          handleChange={handleChange}
                          onEditing={true}
                          // formErrors={formErrors}
                          // setFormErrors={setFormErrors}
                          availableArrearTables={availableArrearTables}
                        />
                      </>
                    )}
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Comisión operación
                    </Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Fijo',
                          'Monto fijo a cobrar por la operación. Reducirá el monto a financiar',
                        ],
                        [
                          'Variable',
                          'Porcentaje a cobrar por la operación. Reducirá el monto a financiar',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Grid container columnSpacing={3}>
                    <Grid item xs={6}>
                      <Typography variant="body1">Fijo ($)</Typography>
                      <TextField
                        value={formatMoney(
                          requestForCreation.operationCostComissionFixed,
                        )}
                        type="text"
                        fullWidth
                        onChange={(e) =>
                          handleChange(
                            'operationCostComissionFixed',
                            e.target.value,
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1">Variable (%)</Typography>
                      <TextField
                        value={
                          requestForCreation.operationCostComissionVariablePercentage
                        }
                        type="number"
                        fullWidth
                        onChange={(e) =>
                          handleChange(
                            'operationCostComissionVariable',
                            e.target.value,
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography fontWeight={600}>Otros gastos</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Fijo',
                          'Valor de cualquier otro gasto en el que se incurra en la operación',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Typography variant="body1">Fijo ($)</Typography>
                  <TextField
                    value={formatMoney(requestForCreation.expensesCost)}
                    type="text"
                    fullWidth
                    onChange={(e) =>
                      handleChange('expensesCost', e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </div>
          </div>
        </Grid>
        <Grid item xs={6}>
          {requestForCreation.productType === 'traditional' && (
            <SimulationTraditional
              requestForCreation={requestForCreation}
              requestConfiguration={requestConfiguration}
              operationDetails={operationDetailsTraditional}
            />
          )}
        </Grid>
      </Grid>
      <Typography fontWeight="bold" padding="0px 0px 6px" variant="h6">
        Detalle documentos
      </Typography>
      <EnhancedTable
        dataHeaders={[
          {
            key: 'folio',
            label: 'Folio',
          },
          {
            key: 'code',
            label: 'Código Doc.',
          },
          {
            key: 'businessIdentifier',
            label: 'Rut Proveedor',
          },
          {
            key: 'businessName',
            label: 'Proveedor',
          },
          {
            key: 'issuedDateShortString',
            label: 'Emisión',
          },
          {
            key: 'amount',
            label: 'Monto Doc.',
            type: 'money',
          },
          {
            key: 'costRate',
            label: 'Dif. precio',
            type: 'money',
          },
          {
            key: 'paymentDateShortString',
            label: 'Vencimiento',
          },
          {
            key: 'daysToCount',
            label: 'Días',
          },
        ]}
        data={requestForCreation.documents.map((document: any) => ({
          ...document,
          costRate: Math.round(
            (document.daysToCount / 30) *
              (requestForCreation.rate / 100) *
              document.amount,
          ),
        }))}
        rowsPerPageDefault={5}
      />
    </div>
  );
};

export default Simulator;
